import { TableView } from "@/types/TableView";
import { apiDelete, apiGet, apiPost, apiPut } from "../_apiShim";
import { TableViewType } from "@/types/enums/TableViewType";

const BASE_PATH = "/api/v1/table-views/";
/**
 * Single source of truth for view projections — ViewFilterHandling imports
 * this (it used to hold a drifting duplicate). `guestDetailLayoutId` and its
 * entity twin `detailLayoutId` must be projected here or the active view
 * seeded on reload never carries the layout link (the backend sanitizer
 * silently strips unprojected fields).
 */
export const VIEWS_FIELDS =
  "tableViewId,name,fields,description,guestDetailLayoutId,detailLayoutId,updatedAt";

// Create a table view
export async function createTableViewEvents(
  tableView: TableView
): Promise<string> {
  return await apiPost<string>(
    `${BASE_PATH}create-table-view-events`,
    {
      name: tableView.name,
      description: tableView.description,
      fields: tableView.fields,
    },
    {
      "Cache-Control": "no-store",
    }
  );
}

// Get table views
export async function getTableViewsEvent(
  excludedIds: string[],
  fields: string,
  page: number = 1,
  size: number = 10
): Promise<TableView[]> {
  const excludedIdsEncoded = excludedIds.map(encodeURIComponent).join(",");

  const response = await apiGet<TableView[]>(
    `${BASE_PATH}get-table-views-events?fields=${encodeURIComponent(
      fields
    )}&page=${encodeURIComponent(page)}&size=${encodeURIComponent(
      size
    )}&excludedIds=${excludedIdsEncoded}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) return [];

  return response;
}

// Update a table view
export async function updateTableViewEvents(
  tableView: TableView
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${tableView.tableViewId}`,
    {
      name: tableView.name,
      description: tableView.description,
      fields: tableView.fields,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function deleteTableViewEvents(
  tableViewId: string
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}/${tableViewId}`, {
    "Cache-Control": "no-store",
  });
}

// Create a table view
export async function createTableViewGuests(
  eventId: string,
  tableView: TableView
): Promise<string> {
  return await apiPost<string>(
    `${BASE_PATH}${eventId}/create-table-view-guests`,
    {
      type: tableView.tableViewType,
      name: tableView.name,
      description: tableView.description,
      fields: tableView.fields,
      // Omit when unset — "" is reserved for "clear" on update.
      ...(tableView.detailLayoutId
        ? { detailLayoutId: tableView.detailLayoutId }
        : {}),
      ...(tableView.guestDetailLayoutId
        ? { guestDetailLayoutId: tableView.guestDetailLayoutId }
        : {}),
    },
    {
      "Cache-Control": "no-store",
    }
  );
}

// Get table views
export async function getTableViewsGuests(
  eventId: string,
  excludedIds: string[],
  fields: string,
  page: number = 1,
  size: number = 10,
  type: TableViewType = TableViewType.GUEST
): Promise<TableView[]> {
  const excludedIdsEncoded = excludedIds.map(encodeURIComponent).join(",");

  const response = await apiGet<TableView[]>(
    `${BASE_PATH}${eventId}/get-table-views-guests?fields=${encodeURIComponent(
      fields
    )}&page=${encodeURIComponent(page)}&size=${encodeURIComponent(
      size
    )}&excludedIds=${excludedIdsEncoded}&type=${type.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) return [];

  return response;
}

// Update a table view
export async function updateTableViewGuests(
  eventId: string,
  tableView: TableView
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}${eventId}/${tableView.tableViewId}`,
    {
      name: tableView.name,
      description: tableView.description,
      fields: tableView.fields,
      // undefined = JSON-omitted = backend keeps the current link;
      // "" = clear ($unset server-side).
      guestDetailLayoutId: tableView.guestDetailLayoutId,
      detailLayoutId: tableView.detailLayoutId,
    },
    {
      "Cache-Control": "no-store",
    },
  );
}

// Delete a table view
export async function deleteTableViewGuests(
  eventId: string,
  tableViewId: string
): Promise<void> {
  await apiDelete<void>(`${BASE_PATH}${eventId}/${tableViewId}`, {
    "Cache-Control": "no-store",
  });
}

// Get a table view by ID
export async function getTableViewEventsById(
  tableViewId: string
): Promise<TableView> {
  const response = await apiGet<TableView>(
    `${BASE_PATH}view/${tableViewId}?fields=${encodeURIComponent(
      VIEWS_FIELDS
    )}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) {
    throw new Error("unknown");
  }

  return response;
}

// Get a table view by ID
export async function getTableViewGuestsById(
  tableViewId: string,
  eventId: string,
  type: TableViewType = TableViewType.GUEST
): Promise<TableView> {
  const response = await apiGet<TableView>(
    `${BASE_PATH}view/${eventId}/${tableViewId}?fields=${encodeURIComponent(
      VIEWS_FIELDS
    )}&type=${type.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) {
    throw new Error("unknown");
  }

  return response;
}

export async function searchTableViewsGuests(
  eventId: string,
  searchTerm: string,
  fields: string,
  page: number = 1,
  size: number = 10,
  type: TableViewType = TableViewType.GUEST
): Promise<TableView[]> {
  const queryParams = new URLSearchParams({
    searchTerm: searchTerm,
    fields: fields,
    page: page.toString(),
    size: size.toString(),
    type: type.toString(),
  });

  const response = await apiGet<TableView[]>(
    `${BASE_PATH}${eventId}/search-guests?${queryParams.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) return [];

  return response;
}

export async function searchTableViewsEvents(
  searchTerm: string,
  fields: string,
  page: number = 1,
  size: number = 10
): Promise<TableView[]> {
  const queryParams = new URLSearchParams({
    searchTerm: searchTerm,
    fields: fields,
    page: page.toString(),
    size: size.toString(),
  });

  const response = await apiGet<TableView[]>(
    `${BASE_PATH}search-events?${queryParams.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true
  );

  if (!response) return [];

  return response;
}
